'use client';

import { useState } from 'react';
import { Calculator, BookOpen, FileText, DollarSign, BarChart3, Lock, TrendingUp, Copy, FileCheck, Wallet, AlertCircle, Settings } from 'lucide-react';
import { ComptabilitatMenu, type MenuGroup } from '@/components/ComptabilitatMenu';
import ChartOfAccounts from './comptabilitat/ChartOfAccounts';
import Journals from './comptabilitat/Journals';
import JournalEntries from './comptabilitat/JournalEntries';
import GeneralLedger from './comptabilitat/GeneralLedger';
import FinancialStatements from './comptabilitat/FinancialStatements';
import PeriodClosing from './comptabilitat/PeriodClosing';
import Reports from './comptabilitat/Reports';
import { AccountingTemplates } from './comptabilitat/AccountingTemplates';
import { ChartSearch } from './comptabilitat/ChartSearch';
import { VatManagement } from './comptabilitat/VatManagement';
import { CashFlowManagement } from './comptabilitat/CashFlowManagement';

type TabValue = 'coa' | 'journals' | 'entries' | 'ledger' | 'statements' | 'closing' | 'reports' | 'templates' | 'search' | 'vat' | 'cashflow' | 'audit' | 'config';

const menuGroups: MenuGroup[] = [
  {
    id: 'Operativa',
    label: 'Operativa',
    description: 'Entrada i gestió diària de dades',
    items: [
      { id: 'journals', label: 'Diaris', icon: BookOpen },
      { id: 'entries', label: 'Assentaments', icon: FileText },
      { id: 'templates', label: 'Plantilles', icon: Copy },
      { id: 'cashflow', label: 'Tresoreria', icon: Wallet },
    ],
  },
  {
    id: 'Fiscal',
    label: 'Fiscal',
    description: 'Compliment tributari i Hisenda',
    items: [
      { id: 'vat', label: 'Llibre d\'IVA', icon: FileText },
      { id: 'reports', label: 'Models Fiscals', icon: TrendingUp },
    ],
  },
  {
    id: 'Analítica',
    label: 'Analítica',
    description: 'Consulta i informació',
    items: [
      { id: 'coa', label: 'Pla de Comptes', icon: Calculator },
      { id: 'search', label: 'Cerca Avançada', icon: FileCheck },
      { id: 'ledger', label: 'Major', icon: DollarSign },
      { id: 'statements', label: 'Estats Financers', icon: BarChart3 },
    ],
  },
  {
    id: 'Supervisió',
    label: 'Supervisió',
    description: 'Auditoria, supervisió i configuració',
    items: [
      { id: 'audit', label: 'Auditoria', icon: AlertCircle },
      { id: 'config', label: 'Configuració', icon: Settings },
      { id: 'closing', label: 'Tancament de Període', icon: Lock },
    ],
  },
];

export default function Comptabilitat() {
  const [activeTab, setActiveTab] = useState<TabValue>('journals');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Comptabilitat</h1>
        <p className="text-muted-foreground">Gestió de comptabilitat, assentaments i estats financers</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Menu - Left Sidebar */}
        <div className="lg:col-span-1">
          <ComptabilitatMenu 
            groups={menuGroups} 
            activeTab={activeTab} 
            onTabChange={(tabId) => setActiveTab(tabId as TabValue)}
          />
        </div>

        {/* Content - Main Area */}
        <div className="lg:col-span-3">
          {activeTab === 'coa' && <ChartOfAccounts />}
          {activeTab === 'journals' && <Journals />}
          {activeTab === 'entries' && <JournalEntries />}
          {activeTab === 'ledger' && <GeneralLedger />}
          {activeTab === 'statements' && <FinancialStatements />}
          {activeTab === 'closing' && <PeriodClosing />}
          {activeTab === 'reports' && <Reports />}
          {activeTab === 'templates' && <AccountingTemplates />}
          {activeTab === 'search' && <ChartSearch />}
          {activeTab === 'vat' && <VatManagement />}
          {activeTab === 'cashflow' && <CashFlowManagement />}
          {activeTab === 'audit' && (
            <div className="p-6 bg-card rounded-lg border border-border">
              <h2 className="text-sm font-semibold mb-2">Auditoria</h2>
              <p className="text-muted-foreground">Registre de canvis i traçabilitat immutable</p>
            </div>
          )}
          {activeTab === 'config' && (
            <div className="p-6 bg-card rounded-lg border border-border">
              <h2 className="text-sm font-semibold mb-2">Configuració Comptable</h2>
              <p className="text-muted-foreground">Paràmetres i configuració del sistema</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
